import React, { useState } from "react";
import { useLanguage } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BookingModal from "../components/BookingModal";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { CheckCircle, Sparkles } from "lucide-react";

const PackagesPage = () => {
  const { t, language } = useLanguage();
  const [bookingModalOpen, setBookingModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <Header onBookingClick={() => setBookingModalOpen(true)} />

      {/* Hero */}
      <section className="pt-28 pb-16 bg-gradient-to-b from-sky-950 to-sky-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="packages-page-title">
            {t.packages.title}
          </h1>
          <p className="text-lg text-sky-200 max-w-2xl mx-auto">{t.packages.subtitle}</p>
        </div>
      </section>

      {/* Accommodation Gallery */}
      <section className="py-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            {language === "es" ? "Nuestros Alojamientos" : language === "de" ? "Unsere Unterkuenfte" : language === "fr" ? "Nos Hebergements" : "Our Accommodations"}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
            <div className="col-span-2 row-span-2">
              <img src="https://customer-assets.emergentagent.com/job_acc6b5c6-4c76-49ba-a1fd-498cb047a8ad/artifacts/ddvw6rgn_IMG_4037.jpeg" alt="Aerial view of Dakhla Kite Planet camp" className="w-full h-full object-cover rounded-xl shadow-lg" />
            </div>
            <div><img src="https://customer-assets.emergentagent.com/job_acc6b5c6-4c76-49ba-a1fd-498cb047a8ad/artifacts/ul9vir1m_IMG_4036.jpeg" alt="Twin bedroom" className="w-full h-48 object-cover rounded-xl shadow-lg" /></div>
            <div><img src="https://customer-assets.emergentagent.com/job_acc6b5c6-4c76-49ba-a1fd-498cb047a8ad/artifacts/4f4h4zgb_IMG_4035.jpeg" alt="Room interior" className="w-full h-48 object-cover rounded-xl shadow-lg" /></div>
            <div><img src="https://customer-assets.emergentagent.com/job_acc6b5c6-4c76-49ba-a1fd-498cb047a8ad/artifacts/nama5ift_IMG_4095.jpeg" alt="Cozy twin bedroom" className="w-full h-48 object-cover rounded-xl shadow-lg" /></div>
            <div><img src="https://customer-assets.emergentagent.com/job_acc6b5c6-4c76-49ba-a1fd-498cb047a8ad/artifacts/xj5pjgh8_IMG_4094.jpeg" alt="Double bedroom" className="w-full h-48 object-cover rounded-xl shadow-lg" /></div>
            <div><img src="https://customer-assets.emergentagent.com/job_acc6b5c6-4c76-49ba-a1fd-498cb047a8ad/artifacts/n2wmn9n0_IMG_4090.jpeg" alt="Traditional room" className="w-full h-48 object-cover rounded-xl shadow-lg" /></div>
            <div><img src="https://customer-assets.emergentagent.com/job_acc6b5c6-4c76-49ba-a1fd-498cb047a8ad/artifacts/5qwue95t_IMG_4092.jpeg" alt="Moroccan dining" className="w-full h-48 object-cover rounded-xl shadow-lg" /></div>
            <div className="col-span-2"><img src="https://customer-assets.emergentagent.com/job_acc6b5c6-4c76-49ba-a1fd-498cb047a8ad/artifacts/gojcjjeg_IMG_4096.jpeg" alt="Evening campfire" className="w-full h-48 object-cover rounded-xl shadow-lg" /></div>
          </div>

          {/* Package Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Premium */}
            <Card className="relative overflow-hidden border-2 hover:border-teal-400 transition-all hover:shadow-2xl transform hover:-translate-y-2 bg-gradient-to-br from-teal-50 to-white" data-testid="package-premium">
              <div className="absolute top-4 right-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-3 py-1 rounded-full text-sm font-semibold">Best Value</div>
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{t.packages.premium.name}</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-teal-600">{t.packages.premium.price}</span>
                  <span className="text-gray-600 ml-2 text-sm">{t.packages.premium.perPerson}</span>
                  <p className="text-sm text-gray-500 mt-1">{t.packages.premium.duration}</p>
                  <p className="text-xs text-gray-600 mt-2 italic">{t.packages.premium.description}</p>
                </div>
                <Badge className="mb-4 bg-teal-600 text-white">{t.packages.premium.badge}</Badge>
                <ul className="space-y-2 mb-8 max-h-60 overflow-y-auto">
                  {t.packages.premium.highlights.map((h, idx) => (
                    <li key={idx} className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" /><span className="text-gray-700 text-sm">{h}</span></li>
                  ))}
                </ul>
                <Button onClick={() => setBookingModalOpen(true)} className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white font-semibold">{t.packages.bookPackage}</Button>
              </CardContent>
            </Card>

            {/* Nomades */}
            <Card className="relative overflow-hidden border-2 hover:border-teal-400 transition-all hover:shadow-2xl transform hover:-translate-y-2" data-testid="package-nomades">
              <div className="absolute top-4 right-4 bg-teal-600 text-white px-3 py-1 rounded-full text-sm font-semibold">Popular</div>
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{t.packages.nomades.name}</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-teal-600">{t.packages.nomades.price}</span>
                  <span className="text-gray-600 ml-2 text-sm">{t.packages.nomades.perPerson}</span>
                  <p className="text-sm text-gray-500 mt-1">{t.packages.nomades.duration}</p>
                  <p className="text-xs text-gray-600 mt-2 italic">{t.packages.nomades.description}</p>
                </div>
                <Badge className="mb-4 bg-teal-600 text-white">{t.packages.nomades.badge}</Badge>
                <ul className="space-y-2 mb-8 max-h-60 overflow-y-auto">
                  {t.packages.nomades.highlights.map((h, idx) => (
                    <li key={idx} className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" /><span className="text-gray-700 text-sm">{h}</span></li>
                  ))}
                </ul>
                <Button onClick={() => setBookingModalOpen(true)} className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white">{t.packages.bookPackage}</Button>
              </CardContent>
            </Card>

            {/* Rental */}
            <Card className="relative overflow-hidden border-2 hover:border-teal-400 transition-all hover:shadow-2xl transform hover:-translate-y-2" data-testid="package-rental">
              <div className="absolute top-4 right-4 bg-teal-600 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1"><Sparkles className="h-3 w-3" />Flexible</div>
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{t.packages.rental.name}</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-teal-600">{t.packages.rental.price}</span>
                  <span className="text-gray-600 ml-2 text-sm">{t.packages.rental.perPerson}</span>
                  <p className="text-sm text-gray-500 mt-1">{t.packages.rental.duration}</p>
                  <p className="text-xs text-gray-600 mt-2 italic">{t.packages.rental.description}</p>
                </div>
                <Badge className="mb-4 bg-teal-600 text-white">{t.packages.rental.badge}</Badge>
                <ul className="space-y-2 mb-8 max-h-60 overflow-y-auto">
                  {t.packages.rental.highlights.map((h, idx) => (
                    <li key={idx} className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" /><span className="text-gray-700 text-sm">{h}</span></li>
                  ))}
                </ul>
                <Button onClick={() => setBookingModalOpen(true)} className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white">{t.packages.bookPackage}</Button>
              </CardContent>
            </Card>

            {/* Surf Lesson */}
            <Card className="relative overflow-hidden border-2 hover:border-teal-400 transition-all hover:shadow-2xl transform hover:-translate-y-2" data-testid="package-surf-lesson">
              <div className="absolute top-4 right-4 bg-teal-600 text-white px-3 py-1 rounded-full text-sm font-semibold">New</div>
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{t.packages.surfLesson.name}</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-teal-600">{t.packages.surfLesson.price}</span>
                  <span className="text-gray-600 ml-2 text-sm">{t.packages.surfLesson.perPerson}</span>
                  <p className="text-sm text-gray-500 mt-1">{t.packages.surfLesson.duration}</p>
                  <p className="text-xs text-gray-600 mt-2 italic">{t.packages.surfLesson.description}</p>
                </div>
                <Badge className="mb-4 bg-teal-600 text-white">{t.packages.surfLesson.badge}</Badge>
                <ul className="space-y-2 mb-8 max-h-60 overflow-y-auto">
                  {t.packages.surfLesson.highlights.map((h, idx) => (
                    <li key={idx} className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" /><span className="text-gray-700 text-sm">{h}</span></li>
                  ))}
                </ul>
                <Button onClick={() => setBookingModalOpen(true)} className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white">{t.packages.bookPackage}</Button>
              </CardContent>
            </Card>

            {/* Surf Guiding */}
            <Card className="relative overflow-hidden border-2 hover:border-teal-400 transition-all hover:shadow-2xl transform hover:-translate-y-2" data-testid="package-surf-guiding">
              <div className="absolute top-4 right-4 bg-teal-600 text-white px-3 py-1 rounded-full text-sm font-semibold">New</div>
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{t.packages.surfGuiding.name}</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-teal-600">{t.packages.surfGuiding.price}</span>
                  <span className="text-gray-600 ml-2 text-sm">{t.packages.surfGuiding.perPerson}</span>
                  <p className="text-sm text-gray-500 mt-1">{t.packages.surfGuiding.duration}</p>
                  <p className="text-xs text-gray-600 mt-2 italic">{t.packages.surfGuiding.description}</p>
                </div>
                <Badge className="mb-4 bg-teal-600 text-white">{t.packages.surfGuiding.badge}</Badge>
                <ul className="space-y-2 mb-8 max-h-60 overflow-y-auto">
                  {t.packages.surfGuiding.highlights.map((h, idx) => (
                    <li key={idx} className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" /><span className="text-gray-700 text-sm">{h}</span></li>
                  ))}
                </ul>
                <Button onClick={() => setBookingModalOpen(true)} className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white">{t.packages.bookPackage}</Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
      <BookingModal isOpen={bookingModalOpen} onClose={() => setBookingModalOpen(false)} />
    </div>
  );
};

export default PackagesPage;
